/**
 * AppRouter - Manifest-driven application router.
 *
 * Replaces RouterConfig.tsx with a cleaner approach:
 * - No runtime find() lookups - components referenced directly from manifest
 * - Routes grouped by workspace and wrapped in appropriate layouts
 * - Same loading/timeout behavior as the original RouterConfig
 */

import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AdminGuard, AdminShell } from '@/features/admin';
import { AUTH_TIMEOUTS } from '@/shared/config/constants';
import { useAuth } from '@/shared/hooks';
import { ErrorBoundary } from '@/shared/ui/error';
import { LoadingScreen } from '@/shared/ui/loading';
import { logger } from '@/shared/utils/logger';
import {
  AuthGuard,
  ClientLayout,
  SoloLayout,
  ShopLayout,
  OnboardingLayout,
} from './guards';
import { lazyWithReload as lazy } from './lazyWithReload';
import { manifest } from './manifest';
import { DashboardShellLayout } from '../layouts/dashboard-shell';

// Lazy load admin pages
const AdminDashboardPage = lazy(() =>
  import('@/features/admin/pages/AdminDashboardPage').then(m => ({
    default: m.AdminDashboardPage,
  })),
);
const AdminProvidersPage = lazy(() =>
  import('@/features/admin/pages/AdminProvidersPage').then(m => ({
    default: m.AdminProvidersPage,
  })),
);
const AdminProviderHealthPage = lazy(() =>
  import('@/features/admin/pages/AdminProviderHealthPage').then(m => ({
    default: m.AdminProviderHealthPage,
  })),
);
const AdminProviderDetailPage = lazy(() =>
  import('@/features/admin/pages/AdminProviderDetailPage').then(m => ({
    default: m.AdminProviderDetailPage,
  })),
);
const AdminShopsPage = lazy(() =>
  import('@/features/admin/pages/AdminShopsPage').then(m => ({
    default: m.AdminShopsPage,
  })),
);
const AdminSubscriptionsPage = lazy(() =>
  import('@/features/admin/pages/AdminSubscriptionsPage').then(m => ({
    default: m.AdminSubscriptionsPage,
  })),
);
const AdminPaymentsPage = lazy(() =>
  import('@/features/admin/pages/AdminPaymentsPage').then(m => ({
    default: m.AdminPaymentsPage,
  })),
);
const AdminReviewsPage = lazy(() =>
  import('@/features/admin/pages/AdminReviewsPage').then(m => ({
    default: m.AdminReviewsPage,
  })),
);
const AdminImageReviewPage = lazy(() =>
  import('@/features/admin/pages/AdminImageReviewPage').then(m => ({
    default: m.AdminImageReviewPage,
  })),
);
const AdminAuditLogsPage = lazy(() =>
  import('@/features/admin/pages/AdminAuditLogsPage').then(m => ({
    default: m.AdminAuditLogsPage,
  })),
);
const AdminServiceCategoriesPage = lazy(() =>
  import('@/features/admin/pages/AdminServiceCategoriesPage').then(m => ({
    default: m.AdminServiceCategoriesPage,
  })),
);
const AdminServiceCategoryDetailPage = lazy(() =>
  import('@/features/admin/pages/AdminServiceCategoryDetailPage').then(m => ({
    default: m.AdminServiceCategoryDetailPage,
  })),
);
const AdminServiceTemplatesPage = lazy(() =>
  import('@/features/admin/pages/AdminServiceTemplatesPage').then(m => ({
    default: m.AdminServiceTemplatesPage,
  })),
);
const AdminServiceTemplateDetailPage = lazy(() =>
  import('@/features/admin/pages/AdminServiceTemplateDetailPage').then(m => ({
    default: m.AdminServiceTemplateDetailPage,
  })),
);

// Route helper IDs for quick lookup
const routeMap = new Map(manifest.map(r => [r.id, r]));

const getRoute = (id: string) => {
  const route = routeMap.get(id);
  if (!route) {
    throw new Error(`Route not found: ${id}`);
  }
  return route;
};

const withRouteBoundary = (element: React.ReactElement) => (
  <ErrorBoundary>{element}</ErrorBoundary>
);

// Lazy load 404 page
const PageNotFound = lazy(() =>
  import('./404').then(m => ({ default: m.PageNotFound })),
);

/**
 * Render a route element from a manifest entry by ID
 */
const RouteElement = ({ id }: { id: string }) => {
  const route = getRoute(id);
  return React.createElement(route.component);
};

export const AppRouter = () => {
  const { isLoading, isAuthenticated } = useAuth();
  const [hasLoadingTimedOut, setHasLoadingTimedOut] = useState(false);

  // Add timeout for loading state as final fallback
  useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        logger.warn('AppRouter loading timeout - forcing redirect to home');
        setHasLoadingTimedOut(true);
      }, AUTH_TIMEOUTS.ROUTER_FALLBACK);

      return () => clearTimeout(timeout);
    } else {
      setHasLoadingTimedOut(false);
    }
  }, [isLoading]);

  // Force redirect if stuck loading for too long
  if (hasLoadingTimedOut) {
    logger.info('Forcing redirect to home due to loading timeout');
    window.location.href = '/';
    return null;
  }

  // Show app-wide loading state if auth is still initializing
  if (isLoading) {
    return <LoadingScreen message="Initializing..." />;
  }

  return (
    <Suspense fallback={<LoadingScreen message="Loading..." />}>
      <Routes>
        {/* ========== PUBLIC ROUTES ========== */}
        {manifest
          .filter(r => r.id.startsWith('public.'))
          .map(route => (
            <Route
              key={route.id}
              path={route.path}
              element={withRouteBoundary(React.createElement(route.component))}
            />
          ))}

        {/* ========== POST-LOGIN ROUTE ========== */}
        <Route
          path="/post-login"
          element={withRouteBoundary(<RouteElement id="auth.postLogin" />)}
        />

        {/* ========== ONBOARDING ROUTES ========== */}
        <Route
          path="/onboarding"
          element={withRouteBoundary(<OnboardingLayout />)}
        >
          <Route
            index
            element={<Navigate to="/onboarding/role-select" replace />}
          />
          <Route
            path="role-select"
            element={withRouteBoundary(
              <RouteElement id="onboarding.roleSelect" />,
            )}
          />
          <Route
            path="solo-setup"
            element={<RouteElement id="onboarding.soloSetup" />}
          />
          <Route
            path="shop-setup/*"
            element={<RouteElement id="onboarding.shopSetup" />}
          />
        </Route>

        {/* ========== SOLO PORTAL ROUTES ========== */}
        <Route element={<DashboardShellLayout />}>
          <Route path="/solo" element={withRouteBoundary(<SoloLayout />)}>
            <Route index element={<Navigate to="/solo/dashboard" replace />} />
            <Route path="dashboard" element={<RouteElement id="solo.dashboard" />} />
            <Route path="appointments" element={<RouteElement id="solo.appointments" />} />
            <Route path="schedule" element={<RouteElement id="solo.schedule" />}>
              <Route index element={<Navigate to="working-hours" replace />} />
              <Route
                path="working-hours"
                element={<RouteElement id="solo.workingHours" />}
              />
              <Route
                path="time-off"
                element={<RouteElement id="solo.timeOff" />}
              />
            </Route>
            <Route
              path="clients"
              element={<RouteElement id="solo.clients" />}
            />
            <Route
              path="clients/:clientId"
              element={<RouteElement id="solo.clientDetail" />}
            />
            <Route
              path="services"
              element={<RouteElement id="solo.services" />}
            />
            <Route
              path="reviews"
              element={<RouteElement id="solo.reviews" />}
            />
            <Route
              path="earnings"
              element={<RouteElement id="solo.earnings" />}
            />
            <Route
              path="settings"
              element={<RouteElement id="solo.settings" />}
            />
            <Route
              path="policies"
              element={<RouteElement id="solo.policies" />}
            />
            <Route
              path="payments"
              element={<RouteElement id="solo.payments" />}
            />
            <Route
              path="billing"
              element={<RouteElement id="solo.billing" />}
            />
            <Route path="*" element={<PageNotFound />} />
          </Route>
        </Route>

        {/* ========== SHOP PORTAL ROUTES ========== */}
        <Route element={<DashboardShellLayout />}>
          <Route
            path="/shops/:slug"
            element={withRouteBoundary(<ShopLayout />)}
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<RouteElement id="shop.dashboard" />} />
            <Route path="appointments" element={<RouteElement id="shop.appointments" />} />
            <Route path="schedule" element={<RouteElement id="shop.schedule" />}>
              <Route index element={<Navigate to="working-hours" replace />} />
              <Route
                path="working-hours"
                element={<RouteElement id="shop.workingHours" />}
              />
              <Route
                path="time-off"
                element={<RouteElement id="shop.timeOff" />}
              />
              <Route
                path="time-off/pending"
                element={<RouteElement id="shop.pendingApprovals" />}
              />
            </Route>
            <Route
              path="clients"
              element={<RouteElement id="shop.clients" />}
            />
            <Route
              path="clients/:clientId"
              element={<RouteElement id="shop.clientDetail" />}
            />
            <Route
              path="services"
              element={<RouteElement id="shop.services" />}
            />
            <Route
              path="reviews"
              element={<RouteElement id="shop.reviews" />}
            />
            <Route
              path="earnings"
              element={<RouteElement id="shop.earnings" />}
            />
            <Route path="staff" element={<RouteElement id="shop.staff" />} />
            <Route
              path="settings"
              element={<RouteElement id="shop.settings" />}
            />
            <Route
              path="profile"
              element={<RouteElement id="shop.profile" />}
            />
            <Route
              path="policies"
              element={<RouteElement id="shop.policies" />}
            />
            <Route
              path="billing"
              element={<RouteElement id="shop.billing" />}
            />
            <Route
              path="payments"
              element={<RouteElement id="shop.payments" />}
            />
            <Route
              path="metrics"
              element={<RouteElement id="shop.metrics" />}
            />
            <Route
              path="revenue"
              element={<RouteElement id="shop.revenue" />}
            />
            <Route path="*" element={<PageNotFound />} />
          </Route>
        </Route>

        {/* ========== CLIENT ROUTES ========== */}
        <Route element={<DashboardShellLayout />}>
          <Route path="/client" element={withRouteBoundary(<ClientLayout />)}>
            <Route
              index
              element={<Navigate to="/client/dashboard" replace />}
            />
            <Route
              path="dashboard"
              element={<RouteElement id="client.dashboard" />}
            />
            <Route
              path="appointments/:appointmentId/reschedule"
              element={<RouteElement id="client.reschedule" />}
            />
            <Route
              path="profile"
              element={<RouteElement id="client.profile" />}
            />
            <Route
              path="bookings"
              element={<RouteElement id="client.bookings" />}
            />
            <Route path="*" element={<PageNotFound />} />
          </Route>
        </Route>

        {/* ========== ADMIN PORTAL ROUTES ========== */}
        <Route
          path="/admin"
          element={withRouteBoundary(
            <AuthGuard>
              <AdminGuard>
                <AdminShell />
              </AdminGuard>
            </AuthGuard>,
          )}
        >
          <Route index element={<AdminDashboardPage />} />
          <Route path="providers" element={<AdminProvidersPage />} />
          <Route path="provider-health" element={<AdminProviderHealthPage />} />
          <Route
            path="providers/:providerId"
            element={<AdminProviderDetailPage />}
          />
          <Route path="shops" element={<AdminShopsPage />} />
          <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
          <Route path="payments" element={<AdminPaymentsPage />} />
          <Route path="reviews" element={<AdminReviewsPage />} />
          <Route path="image-review" element={<AdminImageReviewPage />} />
          <Route
            path="service-categories"
            element={<AdminServiceCategoriesPage />}
          />
          <Route
            path="service-categories/:categoryId"
            element={<AdminServiceCategoryDetailPage />}
          />
          <Route
            path="service-templates"
            element={<AdminServiceTemplatesPage />}
          />
          <Route
            path="service-categories/:categoryId/templates/:templateId"
            element={<AdminServiceTemplateDetailPage />}
          />
          <Route path="audit-logs" element={<AdminAuditLogsPage />} />
          <Route path="*" element={<PageNotFound />} />
        </Route>

        {/* ========== PROVIDER TRANSITIONAL ROUTES ========== */}
        {/* Staff onboarding - auth only (role claims may not be in token yet) */}
        <Route
          path="/provider/staff-onboarding"
          element={withRouteBoundary(
            <AuthGuard>
              <RouteElement id="provider.staffOnboarding" />
            </AuthGuard>,
          )}
        />
        {/* Independent provider setup - auth only */}
        <Route
          path="/provider/independent-setup"
          element={withRouteBoundary(
            <AuthGuard>
              <RouteElement id="provider.independentSetup" />
            </AuthGuard>,
          )}
        />
        {/* Continue later - safe exit for incomplete setups */}
        <Route
          path="/provider/setup/continue-later"
          element={withRouteBoundary(
            <AuthGuard>
              <RouteElement id="provider.setupContinueLater" />
            </AuthGuard>,
          )}
        />
        {/* Shop setup flow - auth only */}
        <Route
          path="/provider/setup/*"
          element={withRouteBoundary(
            <AuthGuard>
              <RouteElement id="provider.setup" />
            </AuthGuard>,
          )}
        />
        {/* Organization picker - requires authentication */}
        <Route
          path="/provider/select-shop"
          element={withRouteBoundary(
            <AuthGuard>
              <RouteElement id="authenticated.selectShop" />
            </AuthGuard>,
          )}
        />

        {/* ========== ERROR ROUTES ========== */}
        <Route
          path="/404"
          element={withRouteBoundary(<RouteElement id="error.notFound" />)}
        />
        <Route
          path="/unauthorized"
          element={withRouteBoundary(<RouteElement id="error.unauthorized" />)}
        />

        {/* ========== FALLBACK ROUTES ========== */}
        <Route
          path="/dashboard"
          element={
            isAuthenticated ? (
              <Navigate to="/404" replace />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="*"
          element={
            isAuthenticated ? (
              <Navigate to="/404" replace />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
      </Routes>
    </Suspense>
  );
};
